import React from 'react';

/**
 * Utility function to create lazy-loaded components with consistent loading behavior
 * This replaces react-loadable with React's built-in React.lazy and Suspense
 *
 * @param {Function} importFunc - Function that returns a dynamic import
 * @param {React.Component} FallbackComponent - Component to show while loading (optional)
 * @returns {React.Component} Lazy component wrapped with Suspense
 */
export const createLazyComponent = (importFunc, FallbackComponent = null) => {
  const LazyComponent = React.lazy(importFunc);

  return props => (
    <React.Suspense
      fallback={
        FallbackComponent == null ? (
          <div>Loading...</div>
        ) : React.isValidElement(FallbackComponent) ? (
          FallbackComponent
        ) : typeof FallbackComponent === 'function' ? (
          React.createElement(FallbackComponent)
        ) : (
          FallbackComponent
        )
      }
    >
      <LazyComponent {...props} />
    </React.Suspense>
  );
};

/**
 * Creates a lazy component that returns null while loading (matches react-loadable behavior)
 * This is useful for components that should not show any loading indicator
 */
export const createLazyComponentSilent = importFunc => {
  return createLazyComponent(importFunc, () => null);
};

export default createLazyComponent;
